import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core'
import type { Notification } from '@awork/framework/models/notification.model'
import { AppService } from '../../../../app.service'
import { resolveNotificationTarget } from '../../../../framework/services/notification-navigation-service/notification-target'
import { NotificationNavigationService } from '../../../../framework/services/notification-navigation-service/notification-navigation.service'
import { NotificationContentComponent } from '../../../../shared/components/notification-content/notification-content.component'
import { animate, style, transition, trigger } from '@angular/animations'
import { SwipeRowComponent } from '../../../../shared/components/swipe-row/swipe-row.component'
import type { SwipeRowAction } from '../../../../shared/components/swipe-row/swipe-row.component'

import { NotificationEvents } from '@awork/framework/models/notification.types'
import type { ValidEntityTypes } from '@awork/framework/models/notification.types'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import { HapticService } from '../../../../shared/services/haptic-service/haptic.service'
import { IconButtonComponent } from '../../../../shared/components/icon-button/icon-button.component'

export type NotificationItemAction =
  { action: 'seen'; navigation?: boolean } | { action: 'unwatch'; entityType: ValidEntityTypes; entityId: string }

@Component({
  selector: 'aw-notification-item',
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.scss'],
  imports: [SwipeRowComponent, NotificationContentComponent, IconButtonComponent],
  animations: [
    trigger('slideClose', [
      transition(':leave', [
        style({ height: '*', overflow: 'hidden' }),
        animate('0.2s ease-out', style({ height: '0', overflow: 'hidden' }))
      ])
    ])
  ]
})
export class NotificationItemComponent {
  private readonly destroyRef = inject(DestroyRef)
  private readonly navigation = inject(NotificationNavigationService)
  private settingsQuery = inject(SettingsQuery)
  appService = inject(AppService)
  private readonly haptics = inject(HapticService)

  readonly notification = input.required<Notification>()
  readonly pending = input(false)
  /** Shows the chevron that expands a conversation with more than one message. */
  readonly groupCollapsible = input(false)
  readonly groupExpanded = input(false)
  readonly groupToggled = output<void>()
  readonly actionExecuted = output<void>()
  readonly notificationAction = output<NotificationItemAction>()
  readonly notificationTitle = computed(() => this.notification().entityName || this.notification().text)
  readonly watchTarget = computed(() => this.getWatchTarget())
  readonly swipeActions = computed<SwipeRowAction[]>(() => this.getSwipeActions())
  protected readonly translations = q.translations.Notifications
  protected readonly commonTranslations = q.translations.common
  readonly showActions = signal(false)

  /**
   * Resolves the same followable entity used by the web notification row.
   * @withSignal notification Current notification.
   * @returns {{ entityType: ValidEntityTypes; entityId: string; label: string } | undefined} Entity to stop following, if supported.
   */
  private getWatchTarget(): { entityType: ValidEntityTypes; entityId: string; label: string } | undefined {
    const notification = this.notification()
    const entityType =
      notification.event === NotificationEvents.DocumentSpaceContributorAdded
        ? 'documentspaces'
        : notification.entityType
    const entityId = entityType === 'documentspaces' ? notification.documentSpaceId : notification.entityId
    const labels: Partial<Record<ValidEntityTypes, string>> = {
      tasks: this.translations.unfollow.task,
      projects: this.translations.unfollow.project,
      documents: this.translations.unfollow.document,
      documentspaces: this.translations.unfollow.documentSpace,
      filters: this.translations.unfollow.view
    }
    const label = labels[entityType]

    if (!entityId || !label) {
      return undefined
    }

    return { entityType, entityId, label }
  }

  /**
   * Offers read and follow actions using the web inbox's category rules.
   * @withSignal notification, watchTarget Current row and followable entity.
   * @returns {SwipeRowAction[]} Actions for the shared swipe tray.
   */
  private getSwipeActions(): SwipeRowAction[] {
    const actions: SwipeRowAction[] = []

    if (!this.notification().seen && !this.notification().seenOn) {
      actions.push({ id: 'seen', label: this.translations.markAsSeen, icon: 'check' })
    }

    const group = this.notification().groupType
    const target = this.watchTarget()

    if (target && group !== 'deadlines' && group !== 'reminders') {
      actions.push({ id: 'unwatch', label: target.label, icon: 'bell-off' })
    }

    return actions
  }

  /**
   * Dispatches an available action without opening the row target.
   * @param {string} action Selected tray action.
   */
  onSwipeAction(action: string): void {
    if (!this.appService.onlineStatus() || this.pending() || !this.swipeActions().some(item => item.id === action)) {
      return
    }

    switch (action) {
      case 'seen':
        this.notificationAction.emit({ action: 'seen' })
        break
      case 'unwatch': {
        const target = this.watchTarget()

        if (target) {
          this.notificationAction.emit({ action: 'unwatch', entityType: target.entityType, entityId: target.entityId })
        }

        break
      }
    }
  }

  /**
   * Keeps a chevron press from highlighting the whole row. A tap on an open row still closes it.
   * @param {Event} event Touch or mouse press on the chevron.
   */
  onTogglePressStart(event: Event): void {
    if (!this.showActions()) {
      event.stopPropagation()
    }
  }

  /**
   * Opens the notification's target.
   * @param {Event} event Row click.
   */
  notificationClicked(event: Event): void {
    event.stopPropagation()

    if (this.showActions() || this.pending() || resolveNotificationTarget(this.notification()).kind === 'inbox') {
      return
    }

    this.navigation.open(this.notification(), false, () => this.onNavigationAccepted())
  }

  /** Marks an accepted navigation read before its row is dismissed.
   * @returns {boolean} Whether the row is still open and its navigation can proceed.
   */
  private onNavigationAccepted(): boolean {
    if (this.destroyRef.destroyed) {
      return false
    }

    this.haptics.buttonClick()

    if (!this.shouldKeepNotificationOnNavigation() && !this.notification().seen) {
      this.notificationAction.emit({ action: 'seen', navigation: true })
    }

    this.actionExecuted.emit()

    return true
  }

  /**
   * Gets whether notifications should remain unseen after navigation.
   * @returns {boolean}
   */
  private shouldKeepNotificationOnNavigation(): boolean {
    const frameworkViewSetting = this.settingsQuery.queryViewSetting('framework')
    return !!frameworkViewSetting()?.keepNotificationOnNavigation
  }
}
